<template>
  <div>
    <Modal
      data-test="add-new-user-modal"
      justifyContent="flex-start"
      textAlign="flex-start"
      class="w-[90vw] h-[80vh] max-w-[580px] max-h-[520px]"
      :show="companyAdminStore.showAddNewUserDialog"
      :bodyFullHeight="true"
      @close="closeModal"
    >
      <template #header>
        <div class="font-bold text-primary">Add New User</div>
      </template>

      <div :style="`width: 700px; height: 100%`">
        <div class="flex flex-row items-center">
          <p>
            Please enter the information of the user you would like to add to your company giving account. They will
            receive an email with instructions. Once they complete the process, their name and email will appear in the
            users list.
          </p>
        </div>
        <div class="flex flex-row items-center pt-4">
          <Form
            v-if="initialValues"
            v-slot="$form"
            :resolver="resolver"
            :initialValues="initialValues"
            :validateOnValueUpdate="true"
            ref="form"
            class="flex justify-center flex-col gap-4 w-full"
            @keydown.prevent.enter="submit"
            v-focustrap
          >
            <div class="flex flex-col gap-1">
              <span>Email Address</span>
              <InputText
                data-test="email-input"
                class="w-full"
                id="email"
                name="email"
                type="text"
                placeholder="Email"
              />
              <Message
                v-if="$form.email?.invalid"
                data-test="email-error"
                severity="error"
                size="small"
                variant="simple"
                >{{ $form.email.error?.message }}</Message
              >
            </div>
          </Form>
        </div>
      </div>

      <template #footer>
        <Button
          data-test="cancel-button"
          variant="link"
          @click="closeModal"
        >
          Cancel
        </Button>
        <Button
          data-test="confirm-button"
          class="w-[120px]"
          :loading="loading"
          @click="submit"
        >
          Confirm
        </Button>
      </template>
    </Modal>
  </div>
</template>

<script setup>
import { ref, computed, defineEmits } from 'vue';
import { useCompanyAdminStore } from '@/stores/CompanyAdminStore.js';
import { useDonorStore } from '@/stores/DonorStore.js';
import Modal from '@/components/Modal.vue';
import { zodResolver } from '@primevue/forms/resolvers/zod';
import { z } from 'zod';
import { useToastStore } from '@/stores/ToastStore.js';

const companyAdminStore = useCompanyAdminStore();
const donorStore = useDonorStore();

const emit = defineEmits(['closed']);

const form = ref(null);

const loading = ref(false);

const resolver = ref(
  zodResolver(
    z.object({
      email: z.string().min(1, { message: 'Email is required.' }).email({ message: 'Invalid email address.' }),
    }),
  ),
);

const initialValues = computed(() => {
  return {
    email: '',
  };
});

const submit = async () => {
  if (form.value.valid) {
    loading.value = true;
    try {
      await companyAdminStore.addNewUser(form.value.states.email.value);
      useToastStore().add({
        severity: 'success',
        summary: 'New user requested',
        detail:
          'Thank you for adding a new user to your company giving account.  The new user you have requested will receive account creation instructions at the email address provided.  Once they have completed the process, their name will appear in your users list.',
        life: 30000,
      });
      closeModal();
    } catch (e) {
      useToastStore().add({
        severity: 'error',
        summary: 'Error',
        detail: e.response
          ? e.response.data.error
          : 'An error occurred while adding a new user. Please try again later.',
        life: 3000,
      });
    } finally {
      loading.value = false;
    }
  }
};

const closeModal = () => {
  companyAdminStore.showAddNewUserDialog = false;
  emit('closed');
};
</script>
